import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, KeyboardShift, Text } from '@bonanza-social/moonlight';
import { SOF_QUESTIONS, SOF_STATE_PERSISTENCE_DELAY } from './SOF.constants';
import {
  InputSubType,
  InputType,
  SOFPayload,
  SOFQuestion as SOFQuestionType,
} from './SOF.types';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import {
  selectSourceOfFundsForm,
  updateSourceOfFunds as updateSourceOfFundsAction,
} from '../../state';
import {
  buildInitialPayload,
  extractDefaultParams,
} from './utils/buildInitialPayload';
import { buildRequestData } from './utils/buildRequestData';
import { BottomSheet, useNavigation } from '@bonanza-social/navigation';
import { getError } from './utils/error';
import SOFQuestions from './SOFQuestions/SOFQuestions';

import SOFSent from './SOFSent/SOFSent';
import { logError } from '@bonanza-social/toolkit/logging';
import { ScrollView, TextInput } from 'react-native';
import { useDebouncedCallback } from '@bonanza-social/toolkit/hooks';
import { ApiError } from '@bonanza-social/toolkit/api';

function SOFComponent() {
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const {
    id: formId,
    isCompleted,
    formJson,
  } = useSelector(selectSourceOfFundsForm) || {};

  const [payload, setPayload] = useState<SOFPayload>(
    buildInitialPayload(formJson),
  );

  const payloadRef = useRef(payload);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const inputRefs = useRef<Record<string, TextInput | null>>({});

  const scrollViewRef = useRef<ScrollView>(null);

  const handleSubmitEditing = useCallback(
    (currentIndex: number) => {
      if (currentIndex < SOF_QUESTIONS.length - 1) {
        const nextQuestion = SOF_QUESTIONS[currentIndex + 1];
        const nextInput = inputRefs.current[nextQuestion.id];

        nextInput?.focus();
      }
    },
    [inputRefs],
  );

  const updateSourceOfFunds = useCallback(
    async (isCompleted: boolean) => {
      try {
        await dispatch(
          updateSourceOfFundsAction({
            id: Number(formId),
            formJson: buildRequestData(payloadRef.current),
            isCompleted,
          }),
        ).unwrap();
      } catch (error) {
        if (!(error instanceof ApiError)) {
          return;
        }

        const { errorCode = '', errorMessages = [] } = error.data || {};

        logError('SOF error', error);

        if (
          errorCode === 'account-source-of-funds-form-not-found' ||
          errorCode === 'account-source-of-funds-form-update-not-allowed'
        ) {
          const [message] = errorMessages;

          if (message) {
            BottomSheet.open({
              namespace: 'common',
              type: 'Info',
              data: {
                textNS: 'common',
                textKey: 'errorTitle',
                text: message,
              },
            });
          }
        }
      }
    },
    [dispatch, formId],
  );

  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);

  const persistState = useDebouncedCallback(
    () => updateSourceOfFunds(false),
    SOF_STATE_PERSISTENCE_DELAY,
  );

  const onCheckboxChange = useCallback(
    (
      questionId: SOFQuestionType['id'],
      controlId: Required<SOFQuestionType>['controlId'],
      value: boolean,
      text: string,
      inputType?: InputType,
    ) => {
      setPayload((currentPayload) => ({
        ...currentPayload,
        [questionId]: {
          ...currentPayload[questionId],
          [controlId]: {
            answer: value ? text : '',
            controlId,
            ...extractDefaultParams(inputType),
          },
        },
      }));
      persistState();
    },
    [persistState],
  );

  const onInputChange = useCallback(
    (
      questionId: SOFQuestionType['id'],
      controlId: Required<SOFQuestionType>['controlId'],
      value: string,
      inputType?: InputType,
      inputSubType?: InputSubType,
    ) => {
      setPayload((currentPayload) => {
        const currentControl = currentPayload[questionId][controlId] || {};
        const updatedControl = { ...currentControl, controlId };

        if (!value && !inputType) {
          updatedControl.answer = '';
          return {
            ...currentPayload,
            [questionId]: {
              ...currentPayload[questionId],
              [controlId]: updatedControl,
            },
          };
        }

        switch (inputType) {
          case InputType.AMOUNT:
            updatedControl.amount = value;
            if (currentControl.incomeType)
              updatedControl.incomeType = currentControl.incomeType;
            updatedControl.answer = currentControl.answer;
            break;

          case InputType.INCOME:
            updatedControl.incomeType = value;
            updatedControl.amount = currentControl.amount;
            updatedControl.answer = currentControl.answer;
            break;

          case InputType.EMPLOYED:
            updatedControl.answer = currentControl.answer;
            updatedControl.jobTitle =
              inputSubType === InputSubType.JOB_TITLE
                ? value
                : currentControl.jobTitle;
            updatedControl.companyName =
              inputSubType === InputSubType.COMPANY_NAME
                ? value
                : currentControl.companyName;
            updatedControl.industry =
              inputSubType === InputSubType.INDUSTRY
                ? value
                : currentControl.industry;
            break;

          case InputType.SELF_EMPLOYED:
            updatedControl.answer = currentControl.answer;
            updatedControl.jobTitle =
              inputSubType === InputSubType.JOB_TITLE
                ? value
                : currentControl.jobTitle;
            updatedControl.companyName =
              inputSubType === InputSubType.COMPANY_NAME
                ? value
                : currentControl.companyName;
            updatedControl.natureOfBusiness =
              inputSubType === InputSubType.NATURE_OF_BUSINESS
                ? value
                : currentControl.natureOfBusiness;
            break;

          default:
            updatedControl.answer = value;
        }

        return {
          ...currentPayload,
          [questionId]: {
            ...currentPayload[questionId],
            [controlId]: updatedControl,
          },
        };
      });

      persistState();
    },
    [persistState],
  );
  const onSubmit = useCallback(async () => {
    const failedQuestion = SOF_QUESTIONS.find(({ id }) => {
      const errors = getError(id, payload);

      const errorValues = Object.values(errors);

      return errorValues.some((value) =>
        Array.isArray(value) ? value.length > 0 : value !== '',
      );
    });

    if (failedQuestion) {
      setShowError(true);

      //scroll to failed question
      const input = inputRefs.current[failedQuestion.id];
      if (input) {
        input.measure((_, y) => {
          scrollViewRef.current?.scrollTo({ y: y, animated: true });

          input?.focus();
        });
      }

      return;
    }

    try {
      setIsLoading(true);

      await updateSourceOfFunds(true);
    } finally {
      setIsLoading(false);
    }
  }, [payload, updateSourceOfFunds]);

  if (isCompleted || isCompleted === undefined) {
    return <SOFSent />;
  }

  return (
    <KeyboardShift
      showsVerticalScrollIndicator={false}
      style={{ flex: 1 }}
      ref={scrollViewRef}
    >
      <Box gap="space-800" paddingBottom="space-600">
        <Text
          variant="body-m-semi"
          color="darkSurfacePrimary"
          locNS="account"
          locKey="SOFFormDescription"
        />

        {SOF_QUESTIONS.map(
          ({ id, controlId, type, subtitle, options }, index) => (
            <SOFQuestions
              inputRef={(el) => {
                if (el) {
                  inputRefs.current[id] = el;
                }
              }}
              id={id}
              key={id}
              type={type}
              subtitle={subtitle}
              options={options}
              answers={payload[id]}
              controlId={controlId}
              number={index + 1}
              error={showError ? getError(id, payload) : {}}
              onCheckboxChange={onCheckboxChange}
              onInputChange={onInputChange}
              onSubmitEditing={() => handleSubmitEditing(index)}
            />
          ),
        )}

        <Box gap="space-200">
          <Button
            size="large"
            variant="primary"
            loading={isLoading}
            onPress={onSubmit}
            locNS="account"
            locKey="submitForm"
          />
          <Button
            size="large"
            variant="secondary"
            onPress={navigation.popToTop}
            locNS="common"
            locKey="goBack"
          />
        </Box>
      </Box>
    </KeyboardShift>
  );
}

export default memo(SOFComponent);
